import React, { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import Chevron from "Utils/Assets/SVGs/Chevron";
import styles from "./HomeIntroHero.module.css";
import { HomeService } from "../../../Logic/HomeService";
import type { IHomeIntroHeroProps } from "@model/Home.model";

const HomeIntroHero: React.FC<IHomeIntroHeroProps> = ({
  slides = [],
  autoplayDelayMs = 7000,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);

  const homeService = new HomeService();

  const handleGoToSlide = (index: number) => {
    homeService.navigateToSlideIndex(
      swiperRef.current,
      index,
      setActiveIndex
    );
  };

  const handleGoToPreviousSlide = () => {
    swiperRef.current?.slidePrev();
  };

  const handleGoToNextSlide = () => {
    swiperRef.current?.slideNext();
  };

  if (!slides.length) {
    return null;
  }

  return (
    <section className={styles.homeIntro} data-testid="home-intro">
      <Swiper
        modules={[Autoplay]}
        slidesPerView={1}
        loop
        autoplay={{ delay: autoplayDelayMs, disableOnInteraction: false }}
        className={styles.heroSwiper}
        onSwiper={(swiper: SwiperType) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper: SwiperType) => {
          setActiveIndex(swiper.realIndex);
        }}
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.id}>
            <article className={styles.heroSlideInner}>
              <img
                src={slide.imageUrl}
                alt={slide.title}
                className={styles.heroImage}
              />

              <div className={styles.heroOverlay}>
                <header className={styles.heroHeaderRow}>
                  {slide.eyebrowLeft && (
                    <span className={styles.heroEyebrowLeft}>
                      {slide.eyebrowLeft}
                    </span>
                  )}
                  {slide.eyebrowRight && (
                    <span className={styles.heroEyebrowRight}>
                      {slide.eyebrowRight}
                    </span>
                  )}
                </header>

                <div className={styles.heroCard}>
                  <div className={styles.heroTextBlock}>
                    <h1 className={styles.heroTitle}>{slide.title}</h1>
                    {slide.subtitle && (
                      <p className={styles.heroSubtitle}>{slide.subtitle}</p>
                    )}
                    {slide.bottomTag && (
                      <p className={styles.heroBottomTag}>{slide.bottomTag}</p>
                    )}
                  </div>

                  <div className={styles.heroControlsRow}>
                    <button
                      type="button"
                      className={`${styles.heroNavBtn} ${styles.heroNavBtnPrev}`}
                      onClick={handleGoToPreviousSlide}
                      aria-label="Previous slide"
                    >
                      <Chevron className={styles.heroChevronPrev} />
                    </button>

                    <button
                      type="button"
                      className={`${styles.heroNavBtn} ${styles.heroNavBtnNext}`}
                      onClick={handleGoToNextSlide}
                      aria-label="Next slide"
                    >
                      <Chevron className={styles.heroChevronNext} />
                    </button>

                    <div className={styles.heroDots}>
                      {slides.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`${styles.heroDot} ${
                            idx === activeIndex ? styles.heroDotActive : ""
                          }`}
                          onClick={() => handleGoToSlide(idx)}
                          aria-label={`Go to slide ${idx + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className={styles.widgetRow} data-testid="home-intro-widget-row">
        {slides.map((slide, idx) => {
          const isActive = idx === activeIndex;
          return (
            <button
              key={slide.id}
              type="button"
              className={`${styles.widgetItem} ${
                isActive ? styles.widgetItemActive : ""
              }`}
              style={{ backgroundImage: `url(${slide.imageUrl})` }}
              onClick={() => handleGoToSlide(idx)}
            >
              <span className={styles.widgetOverlay} />
              <span className={styles.widgetLabel}>{slide.bottomTag}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default HomeIntroHero;
